import { useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { trpc } from "@/trpc/client";
import { getSafeRedirectUrl } from "@/lib/safeRedirect";

/** Survives React Strict Mode remounts (refs reset; module state does not). */
const approveLinkActionStarted = new Set<string>();

/**
 * Determines if the page is genuinely visible to a real user.
 * Guards against:
 *  - Browser prerendering (Speculation Rules / `<link rel=prerender>`)
 *  - Background-tab prefetch
 *  - Iframe-based link scanners (Gmail, Outlook safety checks)
 *  - Speculative navigations that don't receive focus
 */
function isRealUserVisit(): boolean {
  if (typeof document === "undefined") return false;
  // Background tab or hidden page
  if (document.visibilityState !== "visible") return false;
  // Speculation Rules prerendering (Chrome 109+)
  if (
    "prerendering" in document &&
    (document as unknown as { prerendering: boolean }).prerendering
  )
    return false;
  // Iframe-based prefetch (email clients loading links in hidden iframes)
  if (window.self !== window.top) return false;
  // Prerender activation detection via Performance API (activationStart > 0
  // means this page was originally prerendered, then activated).
  const navEntry = performance.getEntriesByType("navigation")[0] as
    | (PerformanceNavigationTiming & { activationStart?: number })
    | undefined;
  if (navEntry?.activationStart && navEntry.activationStart > 0) return false;
  // Require focus — prefetched/prerendered pages never hold focus
  if (!document.hasFocus()) return false;
  return true;
}

export const Route = createFileRoute("/_home/approve-link/$hash")({
  validateSearch: (search: Record<string, unknown>) => ({
    action: search.action === "reject" ? "reject" : "approve",
    redirect_url:
      typeof search.redirect_url === "string" ? search.redirect_url : "",
  }),
  component: ApproveLinkPage,
});

function ApproveLinkPage() {
  const { hash } = Route.useParams();
  const { action, redirect_url: rawRedirectUrl } = Route.useSearch();
  const navigate = useNavigate();

  const approveMutation = useMutation(
    trpc.publicRouterFunction.approveEmail.mutationOptions({
      onSuccess() {
        const redirectUrl = getSafeRedirectUrl(rawRedirectUrl);
        if (redirectUrl) {
          window.location.replace(redirectUrl);
          return;
        }

        void navigate({
          to: "/approve/$hash",
          params: { hash },
          // Marks this as a fresh approval so the confirmation page continues
          // into the app instead of dead-ending (a signed-out visitor gets a
          // dashboard link instead). Revisits of an old link carry no marker
          // and keep their [reject] control.
          search: { redirect_url: rawRedirectUrl, just_approved: true },
          replace: true,
        });
      },
      onError() {
        void navigate({
          to: "/approve/$hash",
          params: { hash },
          search: { redirect_url: rawRedirectUrl, just_approved: false },
          replace: true,
        });
      },
    }),
  );

  const rejectMutation = useMutation(
    trpc.publicRouterFunction.rejectEmail.mutationOptions({
      onSuccess() {
        void navigate({
          to: "/approve/$hash",
          params: { hash },
          search: { redirect_url: rawRedirectUrl, just_approved: false },
          replace: true,
        });
      },
      onError() {
        void navigate({
          to: "/approve/$hash",
          params: { hash },
          search: { redirect_url: rawRedirectUrl, just_approved: false },
          replace: true,
        });
      },
    }),
  );

  // Stable refs so the effect closure always sees the latest mutations
  // without re-running (useMutation returns a new object every render).
  const approveMutationRef = useRef(approveMutation);
  approveMutationRef.current = approveMutation;
  const rejectMutationRef = useRef(rejectMutation);
  rejectMutationRef.current = rejectMutation;

  useEffect(() => {
    if (typeof document === "undefined") return;

    const sessionKey = `${hash}:${action}`;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const fireAction = () => {
      if (cancelled) return;
      if (approveLinkActionStarted.has(sessionKey)) return;
      approveLinkActionStarted.add(sessionKey);

      if (action === "reject") {
        rejectMutationRef.current.mutate({ hash });
      } else {
        approveMutationRef.current.mutate({ hash });
      }
    };

    const scheduleIfVisible = () => {
      if (!isRealUserVisit()) return;

      clearTimeout(timerId);
      timerId = setTimeout(() => {
        if (isRealUserVisit()) {
          fireAction();
        }
      }, 500);
    };

    scheduleIfVisible();

    // Listen for all events that could signal the page becoming "real":
    // visibility change, focus gain, and prerender activation.
    document.addEventListener("visibilitychange", scheduleIfVisible);
    window.addEventListener("focus", scheduleIfVisible);
    if ("prerendering" in document) {
      document.addEventListener("prerenderingchange", scheduleIfVisible);
    }

    return () => {
      cancelled = true;
      clearTimeout(timerId);
      approveLinkActionStarted.delete(sessionKey);
      document.removeEventListener("visibilitychange", scheduleIfVisible);
      window.removeEventListener("focus", scheduleIfVisible);
      if ("prerendering" in document) {
        document.removeEventListener("prerenderingchange", scheduleIfVisible);
      }
    };
  }, [action, hash]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-xl border border-border/70 bg-card/90 px-6 py-8 text-center shadow-[0_24px_80px_rgba(2,6,23,0.18)] backdrop-blur">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/70">
          <Loader2 className="h-7 w-7 animate-spin text-foreground/80" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {action === "reject"
              ? "Rejecting email access"
              : "Approving email access"}
          </p>
          <p className="text-sm text-muted-foreground">
            Please wait while we process your request.
          </p>
        </div>
      </div>
    </div>
  );
}
